import { memo, useEffect, useMemo, useRef, useState } from "react";
import { bool, number, string } from "prop-types";
import { createGlobalStyle } from "styled-components/macro";
import { rem } from "polished";

import { LC_LICENSE } from "../../constants";

import { LiveChatWidget } from "@livechat/widget-react";
import { Helmet } from "react-helmet";

const chatWidgetDelay = 15 * 1000; // 30 seconds
const chatWidgetId = "chat-widget-container"; // DOM element injected by LiveChat script

function Chat({
  authenticated,
  compareDrawerHeight = 0,
  footerOffset = 0,
  hasCompared = false,
  isCompare = false,
  targetDevice,
}) {
  // Offset the LiveChat widget position so that it does not
  // interfere with the compare listings drawer or footer
  const chatWidgetOffset = useMemo(
    () =>
      getChatWidgetOffset(
        compareDrawerHeight,
        footerOffset,
        hasCompared,
        isCompare,
        targetDevice
      ),
    [compareDrawerHeight, footerOffset, hasCompared, isCompare, targetDevice]
  );

  const chatWidgetRef = useRef(null);
  const chatWidgetTimeout = useRef(null);

  // Save DOM element to ref and add loaded class
  function handleReady() {
    chatWidgetRef.current = document.getElementById(chatWidgetId);

    if (!!chatWidgetRef.current) {
      chatWidgetRef.current.classList.add("loaded");
    }
  }

  // Add or remove open class to DOM element
  function handleVisibilityChanged(data) {
    if (!!chatWidgetRef.current && !!data) {
      if (data.visibility === "maximized") {
        chatWidgetRef.current.classList.add("open");
      } else {
        chatWidgetRef.current.classList.remove("open");
      }
    }
  }

  // Delay loading of LiveChat widget to improve page speed metrics
  const [delayed, setDelayed] = useState(true);

  useEffect(() => {
    if (!authenticated) {
      chatWidgetTimeout.current = setTimeout(
        () => setDelayed(false),
        chatWidgetDelay
      );
    } else {
      if (!!chatWidgetTimeout.current) {
        clearTimeout(chatWidgetTimeout.current);
        setDelayed(true);
      }
    }

    return () => {
      if (!!chatWidgetTimeout.current) {
        clearTimeout(chatWidgetTimeout.current);
      }
    };
  }, [authenticated]);

  // End early if there's no LiveChat license or the user is logged in
  if (!LC_LICENSE || authenticated) {
    return null;
  }

  // Otherwise, display the LiveChat widget
  return (
    <>
      <Helmet defer={false}>
        <link rel="preconnect" href="https://cdn.livechatinc.com" />
        <link rel="preconnect" href="https://api.livechatinc.com" />
      </Helmet>
      <ChatWidgetStyles chatWidgetOffset={chatWidgetOffset} />
      {!delayed && (
        <LiveChatWidget
          license={`${LC_LICENSE}`}
          onVisibilityChanged={handleVisibilityChanged}
          onReady={handleReady}
        />
      )}
    </>
  );
}

const ChatWidgetStyles = createGlobalStyle`
  #${chatWidgetId} {
    &.loaded {
      opacity: 1 !important;
      visibility: visible !important;
      z-index: 2147483639 !important;
    }

    &:not(.open) {
      transform: translateY(${({ chatWidgetOffset }) =>
        rem(-chatWidgetOffset + "px")});
      z-index: 999 !important;
    }
  }
`;

/**
 * Get y-offset from bottom of the viewport to the
 * top of the footer or compare listings drawer
 * @param    {number}    compareDrawerHeight
 * @param    {number}    footerOffset
 * @param    {boolean}   hasCompared
 * @param    {boolean}   isCompare
 * @param    {string}    targetDevice
 * @public
 */
function getChatWidgetOffset(
  compareDrawerHeight,
  footerOffset,
  hasCompared,
  isCompare,
  targetDevice
) {
  let chatWidgetOffset = footerOffset;

  if (targetDevice === "desktop" && hasCompared && !isCompare) {
    chatWidgetOffset = compareDrawerHeight + footerOffset;
  }

  if (targetDevice === "mobile") {
    if (hasCompared) {
      const compareOffset = isCompare
        ? compareDrawerHeight * 1.4
        : compareDrawerHeight / 2;
      chatWidgetOffset = !!footerOffset
        ? footerOffset - compareOffset
        : compareDrawerHeight;
    } else {
      chatWidgetOffset = footerOffset * 0.4;
    }
  }

  return chatWidgetOffset;
}

Chat.propTypes = {
  authenticated: bool,
  compareDrawerHeight: number,
  delay: number,
  footerOffset: number,
  hasCompared: bool,
  isCompare: bool,
  targetDevice: string,
};

export default memo(Chat);
